import { AutoAwesome, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import AIFeatureCheckboxes, { AI_FEATURES } from './AIFeatureCheckboxes';
import MultiSelectDropdown from '../elements/MultiSelectDropdown';
import { createAIConfig, updateAIConfig } from '../../modules/aiConfig';
import { useAiProviders } from '../../hooks/useAiProviders';

const getDefaultFeatures = (scope) =>
  Object.fromEntries(
    AI_FEATURES
      .filter((f) => scope === 'org' ? f.orgOnly : !f.orgOnly)
      .map((f) => [f.id, true]),
  );

const APPLY_TO = {
  ORG: 'org',
  SPECIFIC: 'projects',
};

const TokenLimitField = ({ label, hint, value, onChange, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" fontWeight={500} display="block">{label}</Typography>
      <Typography variant="caption" color="text.secondary">{hint}</Typography>
    </Box>
    <TextField
      size="small"
      type="number"
      value={value ?? ''}
      placeholder="No limit"
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10)))}
      inputProps={{ min: 1, style: { textAlign: 'right', width: 80, fontFamily: 'monospace', fontSize: 13 } }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Typography variant="caption" color="text.secondary" noWrap>tokens</Typography>
          </InputAdornment>
        ),
      }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, flexShrink: 0 }}
    />
  </Box>
);

const SectionLabel = ({ title, required, hint }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="subtitle2" fontWeight={600}>
      {title}{required && <span style={{ color: 'red' }}> *</span>}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const KeyModal = ({
  open,
  onClose,
  onSave,
  existingEntries = [],
  editEntry = null,
  showProject = false,
  projects = [],
  updateFn = null,
  createFn = null,
}) => {
  const isEdit = !!editEntry;
  const { providers, loading: providersLoading } = useAiProviders();

  const [selectedProvider, setSelectedProvider] = useState('gemini');
  const [selectedModel, setSelectedModel] = useState('');
  const [applyTo, setApplyTo] = useState(APPLY_TO.ORG);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [features, setFeatures] = useState(() => getDefaultFeatures(APPLY_TO.ORG));
  const [limits, setLimits] = useState({});
  const [dailyTokenLimit, setDailyTokenLimit] = useState(null);
  const [monthlyTokenLimit, setMonthlyTokenLimit] = useState(null);
  const [keyError, setKeyError] = useState('');
  const [projectError, setProjectError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const activeProvider = providers.find((p) => p.id === selectedProvider);
  const availableModels = activeProvider?.models ?? [];

  const pickDefaultModel = (providerId) => {
    const p = providers.find((pr) => pr.id === providerId);
    if (!p) return '';
    return p.models.find((m) => m.recommended)?.id ?? p.models[0]?.id ?? '';
  };

  const pickDefaultProvider = () => {
    const enabled = providers.filter((p) => p.enabled);
    if (enabled.length === 1) return enabled[0].id;
    return 'gemini';
  };

  useEffect(() => {
    if (!open) {
      setKeyError('');
      setProjectError('');
      setIsDirty(false);
      return;
    }

    const effectiveApplyTo = showProject ? (editEntry?.applyTo ?? APPLY_TO.ORG) : APPLY_TO.SPECIFIC;
    const provider = editEntry?.provider ?? editEntry?.llm ?? pickDefaultProvider();
    const model = editEntry?.model ?? pickDefaultModel(provider);

    setSelectedProvider(provider);
    setSelectedModel(model);
    setApplyTo(effectiveApplyTo);
    setSelectedProjects(editEntry?.projectIds ?? []);
    setApiKey('');
    setShowKey(false);
    setDailyTokenLimit(editEntry?.dailyTokenLimit ?? null);
    setMonthlyTokenLimit(editEntry?.monthlyTokenLimit ?? null);
    setKeyError('');
    setProjectError('');
    setIsDirty(false);

    const apiFeatures = Array.isArray(editEntry?.features) ? editEntry.features : [];
    const hydratedFeatures = getDefaultFeatures(effectiveApplyTo);
    apiFeatures.forEach((f) => {
      const featureKey = f.feature_key ?? f.featureKey;
      const uiFeature = AI_FEATURES.find((uf) => uf.featureKey === featureKey || uf.id === featureKey);
      if (uiFeature) hydratedFeatures[uiFeature.id] = f.is_enabled ?? f.isEnabled ?? true;
    });
    setFeatures(hydratedFeatures);

    const featureLimits = {};
    apiFeatures.forEach((f) => {
      const featureKey = f.feature_key ?? f.featureKey;
      const uiFeature = AI_FEATURES.find((uf) => uf.featureKey === featureKey || uf.id === featureKey);
      if (!uiFeature?.limits) return;
      featureLimits[uiFeature.id] = {};
      uiFeature.limits.forEach((cfg) => {
        const val = f[cfg.apiField] ?? f[cfg.field];
        featureLimits[uiFeature.id][cfg.field] = val !== undefined ? val : (cfg.defaultValue ?? null);
      });
    });
    setLimits(featureLimits);
  }, [open, editEntry, providers]);

  const handleProviderChange = (_, val) => {
    if (!val) return;
    setSelectedProvider(val);
    setSelectedModel(pickDefaultModel(val));
    setIsDirty(true);
  };

  const handleApplyToChange = (value) => {
    setApplyTo(value);
    setProjectError('');
    setIsDirty(true);
    if (value === APPLY_TO.ORG) setSelectedProjects([]);
    setFeatures(getDefaultFeatures(value));
    setLimits({});
    setDailyTokenLimit(null);
    setMonthlyTokenLimit(null);
  };

  const takenIds = new Set(
    existingEntries
      .filter((e) => e.id !== editEntry?.id)
      .flatMap((e) => e.projectIds ?? []),
  );

  const hasDuplicateProjects =
    isDirty &&
    showProject &&
    applyTo === APPLY_TO.SPECIFIC &&
    selectedProjects.some((id) => takenIds.has(id));

  const orgEntryExists = existingEntries.some(
    (e) => e.applyTo === APPLY_TO.ORG && e.id !== editEntry?.id,
  );

  const hasOrgConflict =
    (isDirty || !isEdit) && showProject && applyTo === APPLY_TO.ORG && orgEntryExists;

  const handleSave = async () => {
    let valid = true;

    if (!isEdit && !apiKey.trim()) {
      setKeyError('API key is required.');
      valid = false;
    } else if (apiKey.trim() && apiKey.trim().length < 4) {
      setKeyError('Invalid API key.');
      valid = false;
    } else {
      setKeyError('');
    }

    if (showProject && applyTo === APPLY_TO.SPECIFIC && selectedProjects.length === 0) {
      setProjectError('Select at least one project.');
      valid = false;
    } else {
      setProjectError('');
    }

    if (!valid || hasDuplicateProjects || hasOrgConflict) return;

    const featuresList = Object.entries(features).map(([featureId, isEnabled]) => {
      const uiFeature = AI_FEATURES.find((f) => f.id === featureId);
      const entry = { featureKey: uiFeature?.featureKey ?? featureId, isEnabled };
      uiFeature?.limits?.forEach((cfg) => {
        const val = limits[featureId]?.[cfg.field];
        const resolved = (val != null && val !== '')
          ? Number(val)
          : (cfg.nullable ? null : (cfg.defaultValue ?? null));
        entry[cfg.apiField] = resolved;
      });
      return entry;
    });

    const tokenLimitFields = (() => {
      if (!(!showProject || applyTo === APPLY_TO.SPECIFIC)) return {};
      const fields = {};
      const originalDaily = editEntry?.dailyTokenLimit ?? null;
      const originalMonthly = editEntry?.monthlyTokenLimit ?? null;
      if (!isEdit || dailyTokenLimit !== originalDaily) fields.dailyTokenLimit = dailyTokenLimit;
      if (!isEdit || monthlyTokenLimit !== originalMonthly) fields.monthlyTokenLimit = monthlyTokenLimit;
      return fields;
    })();

    const body = isEdit
      ? {
        projectIds: applyTo === APPLY_TO.ORG ? [] : selectedProjects,
        features: featuresList,
        ...tokenLimitFields,
        ...(selectedProvider && { provider: selectedProvider }),
        ...(selectedModel && { model: selectedModel }),
        ...(apiKey.trim() && { newApiKey: apiKey.trim() }),
      }
      : {
        applyTo,
        projectIds: applyTo === APPLY_TO.ORG ? [] : selectedProjects,
        provider: selectedProvider,
        model: selectedModel,
        apiKey: apiKey.trim(),
        features: featuresList,
        ...tokenLimitFields,
      };

    setSaving(true);
    try {
      const res = isEdit
        ? await (updateFn ? updateFn(body) : updateAIConfig(editEntry.id, body))
        : await (createFn ? createFn(body) : createAIConfig(body));

      const configs = updateFn ? [] : (res?.configs ?? []);
      const singleConfig = updateFn ? res : null;
      onSave(configs, isEdit, editEntry?.id, singleConfig);
      setKeyError('');
      setSelectedProjects([]);
      setApplyTo(APPLY_TO.ORG);
      onClose();
    } catch (err) {
      setKeyError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const providerLabel = activeProvider?.label ?? 'AI';
  const showTokenLimits = applyTo === APPLY_TO.SPECIFIC && !showProject;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>
            {isEdit ? 'Edit API Key' : 'Add API Key'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <SectionLabel title="AI Provider" required />
          {providersLoading ? (
            <CircularProgress size={20} />
          ) : (
            <ToggleButtonGroup
              exclusive
              value={selectedProvider}
              onChange={handleProviderChange}
              size="small"
              sx={{ gap: 1, flexWrap: 'wrap' }}
            >
              {providers.map((p) => {
                const btn = (
                  <ToggleButton
                    value={p.id}
                    key={p.id}
                    disabled={!p.enabled}
                    sx={{
                      px: 2.5, py: 0.75,
                      fontSize: '13px', fontWeight: 500,
                      border: '1px solid', borderColor: 'divider',
                      borderRadius: '8px !important', textTransform: 'none',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main', color: '#fff', borderColor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.dark' },
                      },
                      '&.Mui-disabled': {
                        opacity: 0.45,
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    {p.label}
                  </ToggleButton>
                );
                return p.enabled ? btn : (
                  <Tooltip key={p.id} title="Coming soon" placement="top">
                    <span>{btn}</span>
                  </Tooltip>
                );
              })}
            </ToggleButtonGroup>
          )}
        </Box>
        {availableModels.length > 0 && (
          <Box>
            <SectionLabel title="Model" required />
            <Select
              size="small"
              value={selectedModel}
              onChange={(e) => { setSelectedModel(e.target.value); setIsDirty(true); }}
              displayEmpty
              sx={{ minWidth: 240, fontSize: 13 }}
            >
              {availableModels.map((m) => (
                <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>
                  {m.label}
                  {m.recommended && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, color: 'primary.main', fontWeight: 500 }}
                    >
                      (recommended)
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        <Divider />

        {showProject && (
          <Box>
            <SectionLabel
              title="Scope"
              required
              hint="Choose where this API key will be used."
            />
            <RadioGroup value={applyTo} onChange={(e) => handleApplyToChange(e.target.value)}>
              {(!isEdit || applyTo === APPLY_TO.ORG) && (
                <>
                  <FormControlLabel
                    value={APPLY_TO.ORG}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Organisation-wide</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Applies to all org-level AI features, not tied to any specific project.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', mb: 1.5 }}
                  />
                  {hasOrgConflict && (
                    <Alert severity="error" sx={{ py: 0.5, mb: 1.5, ml: 3.5 }}>
                      An organisation-wide key already exists. Delete the existing one before adding a new one.
                    </Alert>
                  )}
                </>
              )}

              {(!isEdit || applyTo === APPLY_TO.SPECIFIC) && (
                <FormControlLabel
                  value={APPLY_TO.SPECIFIC}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Specific projects</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Apply this key to one or more projects.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              )}
            </RadioGroup>

            {applyTo === APPLY_TO.SPECIFIC && (
              <Box sx={{ ml: 3.5, mt: 1 }}>
                <MultiSelectDropdown
                  options={projects}
                  selectedValues={selectedProjects}
                  placeholder="Select projects"
                  onChange={(ids) => {
                    setSelectedProjects(ids);
                    setIsDirty(true);
                    if (ids.length > 0) setProjectError('');
                  }}
                  showSelectAll
                  searchable
                  name="projects"
                  sx={{ mb: 0 }}
                />
                {projectError && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.25 }}>
                    {projectError}
                  </Typography>
                )}
              </Box>
            )}

            {hasDuplicateProjects && (
              <Alert severity="error" sx={{ py: 0.5, mt: 1 }}>
                One or more selected projects already have a key. Deselect them or delete the existing entry first.
              </Alert>
            )}
          </Box>
        )}

        <Box>
          <SectionLabel
            title={`${providerLabel} API Key`}
            required={!isEdit}
            hint={isEdit ? 'Leave blank to keep the existing key unchanged.' : undefined}
          />
          <TextField
            fullWidth
            size="small"
            placeholder={isEdit ? 'Enter new key to replace existing…' : 'Paste your API key…'}
            value={apiKey}
            onChange={(e) => { setApiKey(e.target.value); if (keyError) setKeyError(''); }}
            type={showKey ? 'text' : 'password'}
            error={!!keyError}
            helperText={keyError}
            inputProps={{ style: { fontFamily: 'monospace' } }}
            InputProps={{
              endAdornment: (
                <IconButton size="small" onClick={() => setShowKey((v) => !v)} edge="end">
                  {showKey ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                </IconButton>
              ),
            }}
          />
        </Box>

        <Divider />

        <Box>
          <SectionLabel
            title="Features"
            hint={
              showProject
                ? applyTo === APPLY_TO.ORG
                  ? 'Choose which AI features are enabled at the organisation level.'
                  : 'Choose which AI features are enabled for the selected projects.'
                : 'Choose which AI features are enabled for this project.'
            }
          />
          <AIFeatureCheckboxes
            features={features}
            onChange={(id, checked) => setFeatures((f) => ({ ...f, [id]: checked }))}
            scope={showProject && applyTo === APPLY_TO.ORG ? 'org' : 'project'}
            {...(!showProject && {
              limits,
              onLimitChange: (id, field, val) =>
                setLimits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: val } })),
            })}
          />
        </Box>

        {showTokenLimits && (
          <>
            <Divider />
            <Box>
              <SectionLabel
                title="Token Limits"
                hint="Set a cap on total token usage. Optional - leave blank for unlimited."
              />
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'background.default',
                  border: '0.5px solid', borderColor: 'divider',
                  borderRadius: 1.5,
                  display: 'flex', flexDirection: 'column', gap: 1.5,
                }}
              >
                <TokenLimitField
                  label="Daily limit"
                  hint="Resets every day at midnight UTC"
                  value={dailyTokenLimit}
                  onChange={setDailyTokenLimit}
                  disabled={saving}
                />
                <TokenLimitField
                  label="Monthly limit"
                  hint="Resets on the 1st of each month"
                  value={monthlyTokenLimit}
                  onChange={setMonthlyTokenLimit}
                  disabled={saving}
                />
              </Box>
            </Box>
          </>
        )}

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={hasDuplicateProjects || hasOrgConflict || saving}
          startIcon={saving ? <CircularProgress size={14} /> : null}
        >
          {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KeyModal;